import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { PlanPeriodDto, benefitPlanPeriods } from 'core'

import { Alert, Collapse, CollapseItem, Empty, Icon, Skeleton } from 'common/components'
import { addDaysV2, currentYear, isBefore, nowV2 } from 'common/dateFormatters'
import { useQueryRequestV2 } from 'common/hooks'

import ReportCardButton from './ReportCardButton'
import W2ReportAlert from './w2ReportAlert'

import './styles.less'

type ReportCardProps = { title: 'W-2 amounts' | 'PCORI Reports' }

const ReportCard = ({ title }: ReportCardProps) => {
  const { t: translate } = useTranslation()
  const { data: allPlanPeriods, isLoading } = useQueryRequestV2({
    queryFn: () => benefitPlanPeriods(),
    initialState: [],
  })
  const [planPeriods, setPlanPeriods] = useState<PlanPeriodDto[]>([])
  const [pcoriPlans, setPcoriPlans] = useState<PlanPeriodDto[]>([])

  const now = nowV2()

  useEffect(() => {
    if (allPlanPeriods.length === 0) return

    setPcoriPlans(allPlanPeriods.filter((period) => isBefore(period.endDate, now)))
    setPlanPeriods(allPlanPeriods.filter((period) => isBefore(period.startDate, now)))
  }, [allPlanPeriods])

  return (
    <>
      {title === 'W-2 amounts' && (
        <>
          <Skeleton loading={isLoading}>
            {planPeriods.length > 0 && (
              <div className="mb-16">
                <Collapse borderless defaultActiveKey={1}>
                  <CollapseItem
                    className="ReportCard-collapse"
                    header={`${title} are ready to download`}
                    key={1}
                    content={
                      <div>
                        <div className="color-gray-8 mt-4 mb-16">
                          <div>
                            You must report each eligible employee’s total allowance for the year on IRS Form W-2 in box
                            12 using code FF (regardless of how much they were actually reimbursed).
                          </div>
                          <div>{translate('admin-dashboard.reports.collapse-header-2')}</div>
                        </div>
                        <Alert
                          showIcon
                          type="info"
                          message={'Pro tip about reimbursements after filling out employees W-2 information'}
                          description={
                            <div className="color-gray-8">
                              We recommend as a best practice to not reimburse your employees after you have filled out
                              your employees W-2 information for expenses incurred in {currentYear()} at the end of the
                              year. Wait until the the new year to reimburse these expenses so that they show up on the{' '}
                              {currentYear() + 1} report so you will not need to make W-2 corrections for the current
                              year.
                            </div>
                          }
                        />
                      </div>
                    }
                  />
                </Collapse>
              </div>
            )}

            {planPeriods.map((period) => (
              <ReportCardButton period={period} title={title} />
            ))}

            {planPeriods.length === 0 && (
              <Empty
                description={
                  <>
                    <h6 className="semi-bold">No W2 Reports to download</h6>
                    <p className="color-gray-7">There are no W2 Reports to download</p>
                  </>
                }
              />
            )}
          </Skeleton>
        </>
      )}

      {title === 'PCORI Reports' && (
        <Skeleton loading={isLoading}>
          {pcoriPlans.map((period) => (
            <ReportCardButton period={period} title={title} />
          ))}

          {pcoriPlans.length === 0 && (
            <Empty
              description={
                <>
                  <h6 className="semi-bold">No PCORI Reports to download</h6>
                  <p className="color-gray-7">There are no PCORI Reports to download</p>
                </>
              }
            />
          )}
        </Skeleton>
      )}
    </>
  )
}

export default ReportCard
